import { defineStore } from "pinia";

import { useWindowSize } from '@vueuse/core'
const { width } = useWindowSize()

import { initialize as initializeLocalStorage } from "./helpers/localStorage.mjs";
import { fetchFromBackend } from "./helpers/backend.mjs";

import { useIncentivesStore } from "./incentives";
import { usePerformanceStore } from "./performance";
import { useActivityStore } from "./activity";
import { useRatingsStore } from "./ratings";
import { useBusinessPartnersStore } from "./businessPartners";
import { useBusinessPartnersListsStore } from "./businessPartnersLists";
import { useBusinessPartnersCountStore } from '@/stores/businessPartnersCount'
import { useNotificationStore } from './notification';
import { useAdminStore } from './admin';
import { useLeadGenerationStore } from '@/stores/leadGeneration'

import router from '../router/routes';

import { browserStorage, setKey as setBrowserStorageKey } from "./helpers/localStorage.mjs";

export const useUserStore = defineStore("user", {
  state: () => {
    return {
      id: null,
      sugar: null,
      idCampaignLogin: null,
      loginStatus: null,
      maxReservations: null,
      maxToDos: {
        followUp: 100000,
        feedback: 100000,
      },
      isLoading: false,
      firstName: "",
      lastName: "",
      // Modules returned by login endpoint that control which dashboard previews/widgets are shown
      modules: [],
      tag: "",
      shortcut: "",
      status: {},
      roleList: [],
      listSegments: [],
      theme: "",
      preferences: {
        version: 1,
        widgets: {
          dashboard: {
            showIncentives: true,
            showPerformance: true,
            showActivity: true,
            showRatings: true,
            compactTables: false,
          }
        },
        widgetSettings: {
          incentives: { highlightTop: 3, showTrend: true },
          performance: { showTargets: true, showDelta: true },
          activity: { chartType: 'bar', period: 'week' },
          ratings: { minStars: 3 }
        },
        widgetOrder: ['incentives', 'performance', 'activity', 'ratings'],
        notifications: {
          autoDismissSuccess: true,
          autoDismissDelayMs: 2500,
          maxVisible: 5,
          playSound: false,
          soundVolume: 0.5,
        },
        ui: {
          density: 'comfortable', // comfortable | compact
          language: 'de',
          theme: 'light',
        },
        privacy: {
          sharePerformancePublic: false,
          showEmail: false,
          showAvatar: true,
        }
      },
      currentSelection: {
        module: "dashboard",
        scope: null,
        campaign: {
          isLoading: true,
          id: null,
          company: null,
        },
        incentives: {
          isLoading: true,
          display: "salesAgents",
        },
        ratings: {
          isLoading: true,
          display: "week",
        },
        performance: {
          isLoading: false,
          display: "halftime",
        },
        activity: {
          isLoading: true,
          display: "week",
          week: null,
          month: null,
        },
        leadList: {
          isLoading: true,
          signature: '',
          lists: [],
        },
        leadGeneration: {
          isLoading: false,
          isProcessing: false,
          matchingIsAvailable: false,
          display: "google",
          countResults: 0,
          countProcessed: 0,
          signature: '',
          layout: 'grid',
          source: 'search',
          search: {
            query: '',
            error: false,
          },
        },
        leadMatching: {
          isLoading: false,
          isProcessing: false,
          display: "google",
          countResults: 0,
          countProcessed: 0,
          signature: '',
          layout: 'grid',
          source: 'search',
          search: {
            query: '',
            error: false,
          },
        },
        businessPartnerList: {
          isLoading: true,
          isLoadingToDoFollowUps: false,
          isLoadingToDoAppointments: false,
          isLoadingTodayFollowUps: false,
          isLoadingTodayAppointments: false,
          layout: 'grid', // list or grid
          title: '',
          source: 'search', // sources are either 'search' or 'filter'
          signature: '',
          filter: {
            idBusinessPartnerStatus: null, // previously idSelection 
            idBusinessPartnerAboStatus: -1, // new: the BK status, it wasn't previously part of the filter but did a frontend filter
            idBusinessPartnerList: -1,
            idBusinessPartnerProduct: [], // Array of {id, include}
            idBusinessPartnerProductSegment: [], // Array of {id, include}
            idBusinessPartnerPotential: -1,
            idBusinessPartnerCityZip: -1,
            nameBusinessPartnerSector: '',
            idBusinessPartnerOrder: -1,
            error: false,
            isOpened: false,
          },
          search: {
            query: '',
            mode: 'all',
            error: false,
            isLoading: false,
            // neue Such-Flags für BusinessPartner Suche
            wholeWord: false,
            matchCase: false,
          },
          page: 1,
          pages: 1,
          pageSize: 24,
          maxRows: 0,
          // Ziel-Section im Filter (für Scroll von Summary-Chips)
          scrollToFilterSection: null,
          // Ableitung aus Summary-Chips: welche Bereiche sollen im Filter sichtbar sein?
          activeSummarySections: null,
        },
        businessPartnerCount: {
          isLoading: true,
        },
        businessPartnersLists: {
          isLoading: true,
          signature: '',
          filter: {
            idBusinessPartnerStatus: null, // previously idSelection 
            idBusinessPartnerAboStatus: -1, // new: the BK status, it wasn't previously part of the filter but did a frontend filter
            idBusinessPartnerList: -1,
            // idBusinessPartnerProduct: -1,
            idBusinessPartnerProduct: [], // Array of {id, include}
            idBusinessPartnerProductSegment: [], // Array of {id, include}
            idBusinessPartnerPotential: -1,
            idBusinessPartnerCityZip: -1,
            nameBusinessPartnerSector: '',
            idBusinessPartnerOrder: -1,
            error: false,
            isOpened: false,
          },

        },
        businessPartner: {
          isLoading: false,
          editMode: false,
          additionalSalesPitches: [],
          hasValid: {
            salesPitch: false,
            contactData: false,
          },
          contacts: {
            isLoading: false,
          },
          id: -1,
          // Herkunfts-Route für konsistentes Zurück-Navigieren aus BusinessPartner Details
          origin: null,
        },
        salesAgent: {
          isLoading: false,
          id: -1,
        },
        salesTeam: {
          isLoading: false,
          id: -1,
        },
        feedback: {
          idBusinessPartner: -1,
        }
      },
      colleagues: [],
      campaigns: [],
      filterOptions: {
        isLoading: null,
        options: [],
      },
      _avatarPreload: {
        cache: new Set(),
        inFlight: new Map(),
        enabled: true,
      },

    };
  },
  getters: {
    selectedMedia: (state) => {
      // console.log('current width:', width.value)
      if (width.value < 1024) {
        return 'mobile';
      } else {
        return 'desktop';
      }
    },
    isManagement: (state) => {
      return (state.currentSelection.campaign?.roles?.find((role) => ["SMA:management", "SMA:observer"].includes(role.name)) !== undefined)
    },
    isWinback: (state) => {
      return (state.currentSelection.campaign?.roles?.find((role) => role.name === "SMA:winBack") !== undefined)
    },
    isSalesTeamManager: (state) => {
      return (state.currentSelection.campaign?.roles?.find((role) => role.name === "SMA:salesTeamManager") !== undefined)
    },
    isSalesAgent: (state) => {
      return (state.currentSelection.campaign?.roles?.find((role) => role.name === "SMA:salesAgent") !== undefined)
    },
    availableModules: (state) => state.modules || [],
    hasModule: (state) => {
      return (moduleName) => (state.modules || []).includes(moduleName);
    },
    preventSave: (state) => {
      if (state.isManagement) return true;
      if (state.isSalesTeamManager) return state.id !== state.currentSelection?.salesAgent.idUser;
      return false;
    },
    selectedCampaign: (state) => {
      const campaign = state.data?.find((campaign) => campaign.selected);
      // console.log('selected campaign:', campaign)
      return (campaign) ? campaign : null;
    },
    filterOption: (state) => {
      const filterOptions = state.filterOptions.options.find((filterOption) => filterOption.signature === state.currentSignature);
      // console.log('filterOption:', filterOptions, 'currentSignature:', state.currentSignature)
      return (filterOptions) ? filterOptions.data : null;
    },
    currentFilter: (state) => {
      const scope = state.currentSelection.scope;
      let idSalesTeam = -1;
      let idSalesAgent = -1;
      // Im SalesAgent-Scope immer sowohl Team- als auch Agenten-ID setzen
      if (scope === 'salesAgent') {
        idSalesTeam = state.currentSelection?.salesTeam?.id ?? -1;
        idSalesAgent = state.currentSelection?.salesAgent?.id ?? -1;
      } else if (scope === 'salesTeam') {
        // Im SalesTeam-Scope nur das Team einschränken, Agenten offen lassen
        idSalesTeam = state.currentSelection?.salesTeam?.id ?? -1;
      }
      const filter = {
        idCompany: state.currentSelection?.campaign?.company?.id,
        idCampaign: state.currentSelection?.campaign?.id,
        idSalesTeam,
        idSalesAgent,
      }
      if (state.currentSelection?.businessPartnerList?.filter?.error) state.currentSelection.businessPartnerList.filter.error = null;

      // the filter for the backend needs to be reshabed into regular filter and searchCriteria
      const searchCriteria = []
      const filterSignature = [];
      const filterKeys = [
        'idBusinessPartnerList',
        'idBusinessPartnerStatus',
        'idBusinessPartnerPotential',
        'idBusinessPartnerProduct',
        'idBusinessPartnerProductSegment',
        // Include/Exclude-Maps für Produktfamilien & Produkte explizit in die
        // Signatur aufnehmen, damit Änderungen an "Nur/Ausschließen" als
        // geänderter Filter erkannt werden.
        'produktKombiInclude',
        'produktKombiExclude',
        'produkteInclude',
        'produkteExclude',
        'productGroupStates',
        'revenueMin',
        'revenueMax',
        // NEU als Objekt:
        'businessPartnerSalesPitchStatus', // { id, dateStart, dateEnd }
        'invoice', // { dateStart, dateEnd }
        'order', // ensure 'order' is included for backend
        'salesValues',
        'search', // { mode, string, wholeWord }
      ];
      const searchCriteriaKeys = [
        'idBusinessPartnerAboStatus',
        'idBusinessPartnerCityZip',
        'nameBusinessPartnerSector',
      ]
      for (const key of Object.keys(state.currentSelection?.businessPartnerList?.filter || [])) {
        if (filterKeys.includes(key)) {
          let val = state.currentSelection.businessPartnerList.filter[key];
          // Unwrap Pinia/Vue reactivity for objects
          if (val && typeof val === 'object' && val._custom && val._custom.value) {
            val = val._custom.value;
          } else if (val && typeof val === 'object' && val.value !== undefined && val.value !== null) {
            val = val.value;
          }
          filter[key] = val;
          // For signature: flatten objects as JSON, primitives as is
          if (val && typeof val === 'object') {
            try {
              filterSignature.push(`${key}:${JSON.stringify(val)}`);
            } catch (e) {
              filterSignature.push(`${key}:[object Object]`);
            }
          } else {
            filterSignature.push(`${key}:${val}`);
          }
        } else if (searchCriteriaKeys.includes(key)) {
          const rawVal = state.currentSelection.businessPartnerList.filter[key];
          let searchCriteriaVal = rawVal;
          // "Alle: <Stadt>" in der PLZ/Ort-Filterung liefert nur einen Platzhalter
          // (z.B. "__all_city_Offenburg") statt einer echten PLZ-Liste. Nur für die
          // tatsächliche Backend-Anfrage (searchCriteria) auf die echten PLZ-IDs auflösen —
          // filter/signature behalten den Platzhalter, da die Signatur beim
          // Navigieren/Zurückkehren wieder in den Filter zurückgeparst wird und die
          // Dropdown-Auswahl sonst nicht mehr wiedergefunden werden kann.
          if (key === 'idBusinessPartnerCityZip' && typeof rawVal === 'string' && rawVal.startsWith('__all_city_')) {
            const cityId = rawVal.slice('__all_city_'.length);
            const filterOptionsData = state.filterOptions.options.find((fo) => fo.signature === state.currentSignature)?.data;
            const city = (filterOptionsData?.businessPartnerCityZip || []).find((c) => String(c.id) === String(cityId));
            const zipIds = Array.isArray(city?.zipList) ? city.zipList.map((z) => z.id) : [];
            searchCriteriaVal = zipIds.length ? zipIds.join(',') : -1;
          }
          const criterion = {
            field: key,
            value: searchCriteriaVal
          }
          filter[key] = rawVal;
          searchCriteria.push(criterion);
          filterSignature.push(`${key}:${rawVal}`);
        }
      }
      // Include integrated search in filter and signature if available
      const search = state.currentSelection?.businessPartnerList?.filter?.search;
      if (search && typeof search === 'object' && typeof search.string === 'string' && search.string.trim().length > 0) {
        filter.search = {
          mode: search.mode || 'name',
          string: search.string,
          wholeWord: search.wholeWord
        };
        const ww = typeof search.wholeWord === 'boolean' ? (search.wholeWord ? 1 : 0) : (['1', 'true', 'True'].includes(String(search.wholeWord)) ? 1 : 0);
        const enc = encodeURIComponent(search.string);
        filterSignature.push(`searchMode:${filter.search.mode}`);
        filterSignature.push(`search:${enc}`);
        filterSignature.push(`wholeWord:${ww}`);
      }
      filterSignature.push(`idBusinessPartnerOrder:${state.currentSelection?.businessPartnerList?.filter?.idBusinessPartnerOrder}`);
      filter[`idBusinessPartnerOrder`] = state.currentSelection?.businessPartnerList?.filter?.idBusinessPartnerOrder;

      filter.signature = filterSignature.join('|') + `|${(state.currentSelection.salesAgent?.id) ? state.currentSelection.salesAgent.id : -1}:${state.currentSelection.salesTeam.id}:${state.currentSelection.campaign.id}`;;
      filter.page = state.currentSelection.businessPartnerList.page;
      filter.pages = state.currentSelection.businessPartnerList.pages;
      filter.pageSize = state.currentSelection.businessPartnerList.pageSize;

      // Always set 'order' in view.sort for backend compatibility
      let order = null;
      if (typeof state.currentSelection.businessPartnerList.filter.order !== 'undefined') {
        order = state.currentSelection.businessPartnerList.filter.order;
      }
      const view = {
        sort: {
          field: filter.idBusinessPartnerOrder,
          ...(order ? { order } : {})
        },
        page: state.currentSelection.businessPartnerList.page,
        pageSize: state.currentSelection.businessPartnerList.pageSize,
        searchCriteria: searchCriteria
      };
      // console.log('user filter', filter, 'with signature', filterSignature)
      return { filter, searchCriteria, view };
    },
    currentSignature: (state) => {
      let signature = '';
      // return the existing data if it exists and its update property is less then 6 hours old
      if (state.currentSelection.scope === 'company' && state.currentSelection.campaign.company) {
        signature = `company:${state.currentSelection.campaign.company.id}`
      } else if (state.currentSelection.scope === 'salesTeam' && state.currentSelection.salesTeam) {
        signature = `sT:${state.currentSelection.salesTeam.id}`
      } else if (state.currentSelection.scope === 'salesAgent' && state.currentSelection?.salesAgent && state.currentSelection.campaign.id) {
        signature = `sA:${state.currentSelection?.salesAgent.id}:${state.currentSelection.campaign.id}`
      } else if (state.currentSelection.scope === 'campaign' && state.currentSelection.campaign.id) {
        signature = `campaign:${state.currentSelection.campaign.id}`
      }
      // console.log('SNICKERS: ', state.currentSelection.scope, 'current signature:', signature, 'salesAgent:', state.currentSelection?.salesAgent.id, 'salesTeam:', state.currentSelection.salesTeam.id, 'campaign:', state.currentSelection.campaign.id)
      return signature;
    }
  },
  actions: {
    /** async: fetch the user data from the backend at login
     * @async
    */
    async login(email, colleague = null) {
      const notificationStore = useNotificationStore();
      const adminStore = useAdminStore();
      this.loginStatus = null;
      // letzte Login-Fehlerdetails für Support
      this.lastLoginError = null;

      let response;
      try {
        response = await fetchFromBackend('users/v1/login', { emailAddress: email, loginAs: colleague });
      } catch (error) {
        console.error('Login request failed:', error);
        this.loginStatus = "error";
        this.lastLoginError = {
          code: 'LOGIN_REQUEST_FAILED',
          email,
          colleague,
          message: error?.message || String(error),
        };
        notificationStore.addNotification(
          `Login fehlgeschlagen (Code: ${this.lastLoginError.code}). Technische Details: ${this.lastLoginError.message}`,
          'error',
          21
        );
        return false;
      }

      // Admin-SSO: es kommen nur colleagues zurück, kein regulärer User
      if (response && response.isAdmin && Array.isArray(response.colleagues)) {
        this.loginStatus = "success";
        adminStore.colleagues = response.colleagues;
        adminStore.isAdmin = true;
        return true;
      }

      // Regulärer Login muss einen gültigen User mit positiver ID liefern
      if (!response || typeof response.id !== 'number' || response.id <= 0 || response.code === 'NOT_FOUND' || response.error) {
        console.warn('Login fehlgeschlagen für', email, 'Antwort:', response);
        this.loginStatus = "error";
        const compactResponse = (() => {
          try {
            const clone = { ...response };
            if (clone && typeof clone === 'object') {
              // große Listen entfernen, falls vorhanden
              if (Array.isArray(clone.roleList)) clone.roleList = `[${clone.roleList.length} Rollen]`;
              if (Array.isArray(clone.colleagues)) clone.colleagues = `[${clone.colleagues.length} Kollegen]`;
            }
            return JSON.stringify(clone).slice(0, 400);
          } catch {
            return String(response);
          }
        })();
        this.lastLoginError = {
          code: response?.code === 'NOT_FOUND' ? 'USER_NOT_FOUND' : 'LOGIN_BACKEND_ERROR',
          email,
          colleague,
          backendCode: response?.code,
          backendMessage: response?.message,
          raw: compactResponse,
        };
        notificationStore.addNotification(
          `Login fehlgeschlagen (Code: ${this.lastLoginError.code}). Details: ${this.lastLoginError.backendCode || 'n/a'} - ${this.lastLoginError.backendMessage || compactResponse}`,
          'error',
          21
        );
        return false;
      }

      // notificationStore.addNotification('Login erfolgreich!', 'success');
      this.loginStatus = "success";
      this.maxReservations = response.isSalesAgent?.maxReservations ?? response.maxReservations ?? null;
      this.maxToDos = {
        feedback: (response.isSalesAgent?.maxToDos?.feedback) ? response.isSalesAgent.maxToDos.feedback : 0,
        followUp: (response.isSalesAgent?.maxToDos?.followUp) ? response.isSalesAgent.maxToDos.followUp : 0,
      };
      this.id = response.id;
      this.sugar = response.sugar;
      this.firstName = typeof response.firstName === 'string' ? response.firstName : ''
      this.lastName = typeof response.lastName === 'string' ? response.lastName : ''
      this.database = (response.database) ? response.database : null;
      // Admin Login
      this.tag = (response.tag) ? response.tag : 'user';
      // Build shortcut defensively from available fields
      const ln = (typeof this.lastName === 'string' ? this.lastName : '')
      const fn = (typeof this.firstName === 'string' ? this.firstName : '')
      let initials = ((ln[0] || '') + (fn[0] || '')).toUpperCase();
      if (!initials) {
        const nameStr = typeof response.name === 'string' ? response.name : '';
        if (nameStr) {
          const parts = nameStr.split(/\s+/).filter(Boolean);
          initials = ((parts[0]?.[0] || '') + (parts[1]?.[0] || '')).toUpperCase();
        }
      }
      if (!initials) {
        const email = typeof response.emailAddress === 'string' ? response.emailAddress : '';
        initials = email ? email[0].toUpperCase() : '?';
      }
      this.shortcut = initials;
      this.roleList = response.roleList;
      this.listSegments = response.listSegments;
      this.modules = Array.isArray(response.modules) ? response.modules.slice() : [];
      // the updated property keeps track of the last time the campaigns were updated so the only get updated when necessary
      this.updated = new Date();

      // step 1: get the unique campaigns from the roleList
      for (const role of this.roleList) {
        if (this.campaigns.find(campaign => campaign.id === role.idCampaign)) continue;
        const campaign = role.company.campaignList.find(campaign => campaign.id === role.idCampaign);
        if (!campaign) continue;
        this.campaigns.push({
          id: role.idCampaign,
          name: campaign.name,
          position: campaign.position,
          dateStart: campaign.dateStart,
          dateEnd: campaign.dateEnd,
          publicationList: campaign.publicationList,
          company: {
            id: role.company.id,
            name: role.company.name,
          },
          roles: [],
          selected: false
        });
      }

      // step 2: populate the roles the user has within the campaigns as a user can be both salesAgent and salesTeamManager
      for (const campaign of this.campaigns) {
        const roles = this.roleList.filter(role => role.idCampaign === campaign.id);
        for (const role of roles) {
          campaign.roles.push({
            name: role.name,
            team: role.company.salesTeam,
            salesAgent: role.salesAgent,
          });
        }
      }

      // see if the browser had the campaign stored in local storage where the user was last logged in
      let campaign = null;
      if (browserStorage?.idCamapign) {
        // there is an idCampaignSelected in local storage => see if it is a valid campaign
        campaign = this.campaigns.find(campaign => campaign.id === parseInt(browserStorage.idCamapign));
      } else {
        // if there is no campaign selected in local storage or the campaign doesn't exist any more, find the campaign where the current date is within the dateStart and dateEnd
        const today = new Date(new Date().toDateString()); // today without time
        campaign = this.campaigns.find((campaign) => new Date(campaign.dateStart) <= today && new Date(campaign.dateEnd) >= today);
        // if no campaign is active, select the campaign with the highest position
      }
      if (!campaign && this.campaigns && Array.isArray(this.campaigns) && this.campaigns.length > 0) campaign = this.campaigns.reduce((prev, current) => (prev.position > current.position) ? prev : current);
      this.idCampaignLogin = (campaign.id) ? campaign.id : null;
      if (!campaign) return false;

      // if a salesTeamManager or management role is present, set the scope
      if (campaign.roles?.find((role) => ["SMA:management", "SMA:observer", "SMA:winBack"].includes(role.name)) !== undefined) {
        this.currentSelection.scope = 'salesAgent';

        // als Management oder Observer einloggen => Scope auf Campaign setzen, damit alle SalesTeams und SalesAgents sichtbar sind
        // this.currentSelection.scope = 'campaign';
        // this.currentSelection.salesTeam = { id: -1 };
        // this.currentSelection.salesAgent = { id: -1 }; // Fix: ensure salesAgent is always a clean object
      } else if (campaign.roles?.find((role) => ["SMA:salesTeamManager"].includes(role.name)) !== undefined) {
        this.currentSelection.scope = 'salesAgent';

        // als SalesTeamManager einloggen => Scope auf SalesTeam setzen, damit alle SalesAgents sichtbar sind
        // const teamRole = campaign.roles.find((role) => ["SMA:salesTeamManager"].includes(role.name));

        // this.currentSelection.salesTeam = teamRole && teamRole.team ? teamRole.team : { id: -1 };
        // this.currentSelection.salesAgent = { id: -1 };
        // this.currentSelection.salesTeam = teamRole && teamRole.team ? teamRole.team : { id: -1 };
        // this.currentSelection.salesAgent = { id: -1 }; // Fix: ensure salesAgent is always a clean object
      } else {
        this.currentSelection.scope = 'salesAgent';
      }
      // console.log('scope after login is', this.currentSelection.scope)

      initializeLocalStorage(response.id);
      // load persisted preferences
      this.loadPreferences();
    },
    /** async: on campaign change, refresh all campaign related data
     * @async
     * @param {number} id the id of the campaign that was selected
    */
    async selectCampaign(id = -1) {
      this._isSelectingCampaign = true;
      const incentivesStore = useIncentivesStore();
      const performanceStore = usePerformanceStore();
      const activityStore = useActivityStore();
      const ratingStore = useRatingsStore();
      // const businessPartnerStore = useBusinessPartnersStore();
      const businessPartnersListsStore = useBusinessPartnersListsStore();
      const businessPartnersCountStore = useBusinessPartnersCountStore();
      const notificationStore = useNotificationStore();
      const adminStore = useAdminStore();

      if (adminStore.isAdmin && !adminStore.loginAs) return false;

      try {
        const campaignSelected = this.campaigns.find((campaign) => campaign.id === id);
        if (!campaignSelected) {
          notificationStore.addNotification('Kampagne nicht gefunden!', 'error', 22);
          return false;
        }

        this.currentSelection.campaign = campaignSelected;
        const roleWithSalesTeam = campaignSelected.roles.find((role) => role.team);
        this.currentSelection.salesTeam = (roleWithSalesTeam) ? roleWithSalesTeam.team : { id: -1 };
        // Setze salesAgent explizit auf { id: -1 }, wenn Scope nicht salesAgent
        if (this.currentSelection.scope !== 'salesAgent') {
          this.currentSelection.salesAgent = { id: -1 };
        } else {
          const roleWithSalesAgent = campaignSelected.roles.find((role) => role.name === "SMA:salesAgent");
          this.currentSelection.salesAgent = (roleWithSalesAgent) ? roleWithSalesAgent.salesAgent : { id: -1 };
        }
        this.currentSelection.campaign.isLoading = true;
        this.currentSelection.businessPartnerList.isLoading = false;
        this.campaigns.forEach((campaign) => campaign.selected = false);
        campaignSelected.selected = true;

        try {
          const colleagues = await fetchFromBackend('users/v1/getColleagues', {
            id: this.id,
            filter: {
              // idCompany: response.roleList[0].company.id,
              // idCampaign: response.roleList[0].idCampaign
              idCompany: this.currentSelection.campaign.company.id,
              idCampaign: this.currentSelection.campaign.id
            }
          });
          console.log('colleagues', colleagues)
          this.colleagues = colleagues;
          // Starte asynchrones Vorladen der Kollegen-Avatare (nicht blockierend)
          if (Array.isArray(colleagues) && colleagues.length) {
            queueMicrotask(() => this.preloadColleagueAvatars(colleagues));
          }
        } catch (error) {
          notificationStore.addNotification(`Kollegen konnten nicht geladen werden! (${error?.message || error})`, 'error', 21);
          throw new Error('Kollegen: ' + (error?.message || error));
        }

        try {
          await incentivesStore.refresh();
          // notificationStore.addNotification('Incentives erfolgreich geladen!', 'success');
        } catch (error) {
          notificationStore.addNotification(`Incentives konnten nicht geladen werden! (${error?.message || error})`, 'error', 23);
          throw new Error('Incentives: ' + (error?.message || error));
        }

        if (this.isSalesAgent || this.isSalesTeamManager || this.isManagement) this.currentSelection.performance.isLoading = true;
        this.currentSelection.ratings.isLoading = true;
        this.currentSelection.businessPartnerCount.isLoading = true;

        try {
          // Beim Kampagnenwechsel Filter explizit auf Defaults zurücksetzen
          await this.refreshFilterOptions({ resetFilters: true });
          // notificationStore.addNotification('Filteroptionen erfolgreich geladen!', 'success');
        } catch (error) {
          notificationStore.addNotification(`Filteroptionen konnten nicht geladen werden! (${error?.message || error})`, 'error', 24);
          throw new Error('Filteroptionen: ' + (error?.message || error));
        }

        // Einzelne Task-Refreshes mit eigenem try/catch
        try {
          try {
            await performanceStore.refresh();
            // notificationStore.addNotification('Performance-Daten erfolgreich geladen!', 'success');
          } catch (error) {
            notificationStore.addNotification(`Performance-Daten konnten nicht geladen werden! (${error?.message || error})`, 'error', 25);
            throw new Error('Performance: ' + (error?.message || error));
          }

          try {
            await activityStore.refresh();
            // notificationStore.addNotification('Aktivitätsdaten erfolgreich geladen!', 'success');
          } catch (error) {
            notificationStore.addNotification(`Aktivitätsdaten konnten nicht geladen werden! (${error?.message || error})`, 'error', 26);
            throw new Error('Aktivität: ' + (error?.message || error));
          }

          try {
            await ratingStore.refresh();
            // notificationStore.addNotification('Bewertungsdaten erfolgreich geladen!', 'success');
          } catch (error) {
            notificationStore.addNotification(`Bewertungsdaten konnten nicht geladen werden! (${error?.message || error})`, 'error', 27);
            throw new Error('Bewertung: ' + (error?.message || error));
          }

          try {
            // await businessPartnerStore.refresh();
            // notificationStore.addNotification('BusinessPartner-Daten erfolgreich geladen!', 'success');
          } catch (error) {
            notificationStore.addNotification(`BusinessPartner konnten nicht geladen werden! (${error?.message || error})`, 'error', 28);
            throw new Error('BusinessPartner: ' + (error?.message || error));
          }

          try {
            await businessPartnersListsStore.refresh();
            // notificationStore.addNotification('BusinessPartner-Listen erfolgreich geladen!', 'success');
          } catch (error) {
            notificationStore.addNotification(`BusinessPartner-Listen konnten nicht geladen werden! (${error?.message || error})`, 'error', 29);
            throw new Error('BusinessPartnerLists: ' + (error?.message || error));
          }

          try {
            await businessPartnersCountStore.refresh();
            // notificationStore.addNotification('BusinessPartner-Zähler erfolgreich geladen!', 'success');
          } catch (error) {
            notificationStore.addNotification(`BusinessPartner-Zähler konnten nicht geladen werden! (${error?.message || error})`, 'error', 30);
            throw new Error('BusinessPartnerCount: ' + (error?.message || error));
          }

          try {
            await this.refreshFollowUps();
            await this.refreshAppointments();
          } catch (e) {
            // Fehler beim Reminder-Laden ignorieren, Login geht trotzdem weiter
            notificationStore.addNotification(` (${e?.message || e})`, 'error', 30);
            throw new Error('BusinessPartnerCount: ' + (e?.message || e));
          }

        } catch (error) {
          // Hier kommt nur an, wenn einer der oberen Fehler geworfen wurde
          throw error;
        }

        this.currentSelection.campaign.isLoading = false;
        notificationStore.addNotification('Kampagne erfolgreich geladen!', 'success');
        setBrowserStorageKey("idCampaignSelected", id);
        this._isSelectingCampaign = false;

      } catch (error) {
        notificationStore.addNotification(`Kampagnenwechsel fehlgeschlagen! (${error?.message || error})`, 'error', 31);
        this._isSelectingCampaign = false;
        throw error;
      }
    },
    /** Preload colleague avatar images to reduce perceived latency in admin user select */
    async preloadColleagueAvatars(list) {
      // Avatar-Preload ist deaktiviert, Bilder werden nur bei Bedarf geladen
      return;
      const cdnBase = 'https://sma-sellwerk.fra1.cdn.digitaloceanspaces.com/user/';
      const variants = (id) => [
        `user_${id}.jpg`
      ];
      const toLoad = [];
      for (const c of list) {
        const id = c?.id; if (!id) continue;
        for (const v of variants(id)) {
          if (!this._avatarPreload.cache.has(v)) toLoad.push(v);
        }
      }
      const concurrency = 4;
      const loadImage = (name) => {
        if (this._avatarPreload.inFlight.has(name)) return this._avatarPreload.inFlight.get(name);
        const p = new Promise((resolve) => {
          const img = new Image();
          const done = () => { this._avatarPreload.cache.add(name); this._avatarPreload.inFlight.delete(name); resolve(true); };
          img.onload = done; img.onerror = done; img.decoding = 'async';
          img.src = cdnBase + name;
        });
        this._avatarPreload.inFlight.set(name, p);
        return p;
      };
      async function runPool(files, limit) {
        const executing = new Set();
        for (const f of files) {
          const p = loadImage(f);
          executing.add(p);
          p.finally(() => executing.delete(p));
          if (executing.size >= limit) {
            await Promise.race(executing);
          }
        }
        await Promise.allSettled(executing);
      }
      try { await runPool(toLoad, concurrency); } catch (e) { /* ignore */ }
    },
    /** async: if a salesTeamManager or management selects a single salesAgent, refresh all salesAgent related data
     * @async
     * @param {number} id the id of the salesAgent that was selected
    */
    async selectSalesAgent(id = -1) {
      const notificationStore = useNotificationStore();
      const businessPartnersCountStore = useBusinessPartnersCountStore();
      try {
        // check if the salesAgent exists in the current campaign
        const salesAgentSelected = this.filterOption.salesAgents.find((salesAgent) => salesAgent.id == id);
        // Fallback: Wenn nicht gefunden, auf -1 setzen
        if (!salesAgentSelected) {
          this.currentSelection.salesAgent = { id: -1 };
          this.currentSelection.salesTeam = { id: -1 };
          this.currentSelection.scope = 'salesAgent';
          notificationStore.addNotification('SalesAgent nicht gefunden!', 'error', 32);
          return false;
        }
        this.currentSelection.salesAgent = salesAgentSelected;
        this.currentSelection.salesTeam = { id: -1 }; // Immer zurücksetzen!
        this.currentSelection.scope = 'salesAgent';
        // Count-Spinner für Dashboard/Preview sofort aktivieren
        this.currentSelection.businessPartnerCount.isLoading = true;

        this.currentSelection.businessPartnerList.isLoading = true;
        // Spinner sofort anzeigen
        const leadGenerationStore = useLeadGenerationStore();
        leadGenerationStore.isLoadingEigenrechercheCount = true;
        // Refresh Eigenrecherche count after stores refresh
        await leadGenerationStore.fetchEigenrechercheCount();
        leadGenerationStore.isLoadingEigenrechercheCount = false;
        // the filter needs to be refreshed if the salesAgent changes as some options are salesAgent specific like lists
        await this.refreshFilterOptions({ resetFilters: true });
        await this.refreshStores();
        // Counter für Today/ToDo-Widgets neu laden (steuert u.a. Lade-Spinner in Preview-Cards)
        try {
          await businessPartnersCountStore.refresh();
        } catch (e) {
          // Fehler beim Count-Refresh hier nicht blockierend behandeln
        }
        // Nach Auswahl eines SalesAgents Reminder- und ToDo-Listen (Today/ToDo) aktualisieren,
        // damit Dashboard-Widgets und Preview-Cards (z.B. PreviewBusinessPartnerList) korrekt umschalten
        try {
          await this.refreshFollowUps();
          await this.refreshAppointments();
        } catch (e) {
          // Reminder-Fehler hier nicht blockierend behandeln
        }
        notificationStore.addNotification('SalesAgent-Daten erfolgreich geladen!', 'success');
        setBrowserStorageKey("idSalesAgentSelected", id);
        setBrowserStorageKey("idSalesTeamSelected", -1);
      } catch (error) {
        this.currentSelection.salesAgent = { id: -1 };
        this.currentSelection.salesTeam = { id: -1 };
        this.currentSelection.scope = 'salesAgent';
        notificationStore.addNotification('SalesAgent-Auswahl fehlgeschlagen!', 'error', 32);
        throw error;
      }
    },
    /** async: if a management role selects a salesTeam (Area), refresh all related data
     * @async
     * @param {number} id the id of the salesTeam that was selected
    */
    async selectSalesTeam(id = -1) {
      const notificationStore = useNotificationStore();
      const businessPartnersCountStore = useBusinessPartnersCountStore();
      try {
        // check if the salesTeam exists in the current campaign
        const salesTeamSelected = this.filterOption.salesTeams.find((salesTeam) => salesTeam.id == id);
        // console.log('SALESTEAMSELECTED:', id)
        // console.log('SALESTEAMSELECTED:', salesTeamSelected)
        if (!salesTeamSelected) return false;
        // console.log('select salesTeam with id', id, 'looking for in', this.filterOption.salesTeams, 'and found', salesTeamSelected)
        this.currentSelection.salesTeam = salesTeamSelected;
        this.currentSelection.scope = 'salesTeam';

        // Count-Spinner für Dashboard/Preview sofort aktivieren
        this.currentSelection.businessPartnerCount.isLoading = true;


        this.currentSelection.businessPartnerList.isLoading = true;
        // Erste Ladung der Filteroptionen im SalesTeam-Scope inkl. Filter-Reset
        await this.refreshFilterOptions({ resetFilters: true });
        // clear the selected salesAgent to only get the salesTeam specific data
        this.currentSelection.salesAgent = { id: -1 };

        this.currentSelection.businessPartnerList.isLoading = true;
        // Zweiter Aufruf nur zur Aktualisierung der Optionen, ohne erneut den Filter zurückzusetzen
        await this.refreshFilterOptions({ resetFilters: false });
        // notificationStore.addNotification('Filteroptionen für SalesTeam erfolgreich geladen!', 'success');
        router.push({ name: 'Dashboard' });
        await this.refreshStores();
        // Counter für Today/ToDo-Widgets neu laden
        try {
          await businessPartnersCountStore.refresh();
        } catch (e) {
          // Fehler beim Count-Refresh hier nicht blockierend behandeln
        }
        // Reminder- und ToDo-Listen auch für SalesTeam-Scope aktualisieren
        // Reminder- und ToDo-Listen auch für SalesTeam-Scope aktualisieren
        try {
          await this.refreshFollowUps();
          await this.refreshAppointments();
        } catch (e) {
          // Reminder-Fehler hier nicht blockierend behandeln
        }
        notificationStore.addNotification('SalesTeam-Daten erfolgreich geladen!', 'success');
        setBrowserStorageKey("idSalesTeamSelected", id);
        setBrowserStorageKey("idSalesAgentSelected", -1);
      } catch (error) {
        notificationStore.addNotification('SalesTeam-Auswahl fehlgeschlagen!', 'error', 33);
        throw error;
      }
    },
    /** async: if a management role selects the whole company refresh all related data
     * @async
    */
    async selectCompany() {
      const notificationStore = useNotificationStore();
      const businessPartnersCountStore = useBusinessPartnersCountStore();

      // console.log('select the whole company')
      this.currentSelection.salesAgent = { id: -1 };
      this.currentSelection.salesTeam = { id: -1 };
      // use 'campaign' scope to represent the entire publisher/company (original behavior)
      this.currentSelection.scope = 'campaign';

      try {
        // filterOptions were already loaded for the campaign scope; no refresh needed here
        // Count-Spinner für Dashboard/Preview sofort aktivieren
        this.currentSelection.businessPartnerCount.isLoading = true;
        await this.refreshStores();
        // Counter für Today/ToDo-Widgets neu laden
        try {
          await businessPartnersCountStore.refresh();
        } catch (e) {
          // Fehler beim Count-Refresh hier nicht blockierend behandeln
        }
        // Reminder- und ToDo-Listen für Gesamtfirma aktualisieren
        try {
          await this.refreshFollowUps();
          await this.refreshAppointments();
        } catch (e) {
          // Reminder-Fehler hier nicht blockierend behandeln
        }
        notificationStore.addNotification('Standortdaten erfolgreich geladen!', 'success');
        setBrowserStorageKey("idSalesTeamSelected", -1);
        setBrowserStorageKey("idSalesAgentSelected", -1);
      } catch (error) {
        notificationStore.addNotification('Firmen-Auswahl fehlgeschlagen!', 'error', 34);
        throw error;
      }
    },
    /** async: whenever salesAgent, salesTeam or company is selected, refresh all related data
     * @async
    */
    async refreshStores() {
      const incentivesStore = useIncentivesStore();
      const performanceStore = usePerformanceStore();
      const activityStore = useActivityStore();
      const ratingStore = useRatingsStore();
      const businessPartnerStore = useBusinessPartnersStore();
      const businessPartnersListsStore = useBusinessPartnersListsStore();
      const notificationStore = useNotificationStore();

      // refresh all salesAgent related stores in parallel to save some time
      try {
        let taskList = [];
        taskList.push(incentivesStore.refresh());
        taskList.push(performanceStore.refresh());
        taskList.push(activityStore.refresh());
        taskList.push(ratingStore.refresh());
        await Promise.all(taskList);
        // notificationStore.addNotification('Allgemeine Daten erfolgreich geladen!', 'success');
        await Promise.all([businessPartnerStore.refresh(), businessPartnersListsStore.refresh()]);
        // notificationStore.addNotification('BusinessPartner-Daten erfolgreich geladen!', 'success');
      } catch (error) {
        notificationStore.addNotification('Fehler beim Laden der Stores!', 'error', 35);
        throw error;
      }
    },
    /** async: is used to swich the displayed data for various modules i.e. allows to switch from 'halftime' to 'campaign' on the performance module
     * @async
     * @param {string} module the desired module that should display the data
     * @param {string} displayStatus the desired display status
    */
    async selectDisplayStatus(module, displayStatus) {
      this.currentSelection[module].display = displayStatus;
    },
    /** widget preference helpers **/
    setWidgetPreference(section, key, value) {
      if (!this.preferences.widgets[section]) this.preferences.widgets[section] = {};
      this.preferences.widgets[section][key] = value;
      this.persistPreferences();
    },
    setPreference(group, key, value) {
      if (!this.preferences[group]) this.preferences[group] = {};
      this.preferences[group][key] = value;
      this.persistPreferences();
    },
    exportPreferences() {
      return JSON.stringify(this.preferences, null, 2);
    },
    importPreferences(json) {
      try {
        const data = JSON.parse(json);
        this.preferences = Object.assign({}, this.preferences, data);
        this.persistPreferences();
        return true;
      } catch (e) { return false; }
    },
    resetPreferences() {
      const defaults = {
        version: 1,
        widgets: { dashboard: { showIncentives: true, showPerformance: true, showActivity: true, showRatings: true, compactTables: false } },
        widgetSettings: {
          incentives: { highlightTop: 3, showTrend: true },
          performance: { showTargets: true, showDelta: true },
          activity: { chartType: 'bar', period: 'week' },
          ratings: { minStars: 3 }
        },
        widgetOrder: ['incentives', 'performance', 'activity', 'ratings'],
        notifications: { autoDismissSuccess: true, autoDismissDelayMs: 2500, maxVisible: 5, playSound: false, soundVolume: 0.5 },
        ui: { density: 'comfortable', language: 'de', theme: 'light' },
        privacy: { sharePerformancePublic: false, showEmail: false, showAvatar: true }
      };
      this.preferences = defaults;
      this.persistPreferences();
    },
    setWidgetConfig(key, cfg) {
      if (!this.preferences.widgetSettings) this.preferences.widgetSettings = {};
      this.preferences.widgetSettings[key] = Object.assign({}, this.preferences.widgetSettings[key] || {}, cfg);
      this.persistPreferences();
    },
    setWidgetOrder(order) {
      this.preferences.widgetOrder = Array.isArray(order) ? order.slice() : [];
      this.persistPreferences();
    },
    persistPreferences() {
      try { setBrowserStorageKey('preferences', this.preferences); } catch (e) { }
    },
    loadPreferences() {
      try {
        // new consolidated key
        if (browserStorage?.preferences) {
          this.preferences = Object.assign({}, this.preferences, browserStorage.preferences);
        } else {
          // backwards compatibility for old per-section widget key(s)
          const legacy = browserStorage?.['pref:widgets:dashboard'];
          if (legacy) this.preferences.widgets.dashboard = Object.assign({}, this.preferences.widgets.dashboard, legacy);
        }
      } catch (e) { }
    },
    /** see if the filterOptions need to be fetched again, either because the data is not up to date or the salesAgent has changed
    *  @param {Object} options
    *  @param {boolean} options.resetFilters - whether businessPartner filters should be reset after options refresh
    */
    async refreshFilterOptions({ resetFilters = false } = {}) {
      const notificationStore = useNotificationStore();
      try {
        // console.log('filterOptions', this.filterOptions, 'isLoading', this.filterOptions.isLoading, 'currentSelection', this.currentSelection)
        let filterOptionsExists = null;
        // filterOptionsExists = null // 
        filterOptionsExists = this.filterOptions.options.find((p) => p.signature === this.currentSignature);
        // console.log('SNICKERS: 1: refreshing filter options', filterOptionsExists, this.currentSignature)
        if (filterOptionsExists && filterOptionsExists?.update > (Date.now() - (1000))) {
          // Nur zurückgeben; Filter-Zustand NICHT erneut zurücksetzen
          return filterOptionsExists;
        }
        // console.log('SNICKERS: 1: AA refreshing filter options -- AFTER IF', filterOptionsExists, this.currentSignature)

        // fetch the data from the backend
        const endpoint = "businessPartners/v1/getFilterOptions";
        this.filterOptions.isLoading = true;
        const response = await fetchFromBackend(endpoint, {
          id: this.id,
          filter: {
            idCompany: this.currentSelection.campaign.company.id,
            idCampaign: this.currentSelection.campaign.id,
            idSalesAgents: (this.currentSelection.scope !== 'salesAgent' || !this.currentSelection?.salesAgent?.id) ? -1 : this.currentSelection?.salesAgent.id,
            idSalesTeams: (this.currentSelection.scope !== 'salesTeam') ? -1 : this.currentSelection.salesTeam.id,
          }
        });
        this.filterOptions.isLoading = false;
        // console.log('SNICKERS: 2: refreshing filter options', response)
        if (!response.filterOptions) return false;
        // console.log('SNICKERS: 2: refreshing filter options -- AFTER IF', response)
        // to streamline some options, expand the response
        let data = Object.assign({ businessPartnerCityZips: [], businessPartnerLists: [] }, response.filterOptions);
        // create the cityZip options
        data.businessPartnerCityZips = [];
        for (const city of response.filterOptions.businessPartnerCityZip) {
          if (city.zipList?.length === 0) {
            data.businessPartnerCityZips.push({ id: -1, name: city.name });
            continue;
          } else if (city.zipList?.length > 0) {
            data.businessPartnerCityZips.push({
              id: city.zipList.map((zip) => zip.id).join(','),
              name: city.name,
            })
            for (const zipCode of city.zipList) {
              data.businessPartnerCityZips.push({
                id: zipCode.id,
                name: `${zipCode.name}`,
                isZipCode: true
              });
            }
          }
        }
        // create the lists (Kundenlisten i.e. Abo > 500€) options
        data.businessPartnerLists = [];
        for (const segment of response.filterOptions.businessPartnerSegment) {
          if (segment.lists.length === 0) {
            data.businessPartnerLists.push({
              id: segment.id,
              name: segment.name
            });
          } else {
            data.businessPartnerLists.push({
              id: segment.lists.map((list) => `list:${list.id}`).join(','),
              name: segment.name
            });
            for (const list of segment.lists) {
              data.businessPartnerLists.push({
                id: `list:${list.id}`,
                name: `${list.name}`,
                isList: true
              });
            }
          }
        }
        // create the options for products and potential
        for (const section of ['businessPartnerProduct', 'businessPartnerPotential']) {
          data[section] = [];
          for (const item of response.filterOptions[section]) {
            data[section].push({
              id: (item.id === -1) ? -1 : `product:${item.id}`,
              name: item.name,
              useInBusinessPartnerFilter: (section === 'businessPartnerProduct') ? true : item.useInBusinessPartnerFilter,
            });
          }
        }

        // if the data exists update it, if not create it
        if (filterOptionsExists) {
          filterOptionsExists.data = data;
          filterOptionsExists.update = Date.now();
        } else {
          // console.log('SNICKERS: 3: refreshing filter options', this.currentSignature, this.currentSelection.scope, this.isSalesAgent, this.isSalesTeamManager, this.isManagement, this.currentSelection.campaign.company.id)
          if (this.currentSelection.scope === 'salesAgent' && this.isSalesAgent) {
            let selectedSalesAgent = null
            if (this.isSalesTeamManager || this.isManagement) {
              selectedSalesAgent = response.filterOptions.salesAgents.find((salesAgent) => salesAgent.id === this.currentSelection?.salesAgent?.id);
            } else {
              selectedSalesAgent = response.filterOptions.salesAgents.find((salesAgent) => salesAgent.idUser === this.id);
            }
            // console.log('selectedSalesAgent:', selectedSalesAgent)
            if (selectedSalesAgent) {
              selectedSalesAgent.selected = true;
              this.currentSelection.salesAgent = selectedSalesAgent
              // console.log('selectedSalesAgent:', selectedSalesAgent)
              // this.currentSelection.salesTeam = null
            }
          } else if (this.currentSelection.scope === 'salesTeam' && (this.isSalesTeamManager || this.isManagement)) {
            const selectedSalesTeam = response.filterOptions.salesTeams.find((salesTeam) => salesTeam.id === this.currentSelection.salesTeam?.id);
            if (selectedSalesTeam) {
              selectedSalesTeam.selected = true;
              this.currentSelection.salesTeam = selectedSalesTeam
              // this.currentSelection.salesAgent = null
            }
          } else if (this.currentSelection.scope === 'company' && this.isManagement) {
            const selectedCompany = response.filterOptions.companies.find((company) => company.id === this.currentSelection.campaign.company.id);
            if (selectedCompany) {
              selectedCompany.selected = true;
              this.currentSelection.campaign.company = selectedCompany
            }
          }
          this.filterOptions.options.find((p) => p.signature === this.currentSignature);
          this.filterOptions.options.push({
            // signature: `${this.currentSelection.salesTeam.id}:${this.currentSelection?.salesAgent?.id}:${this.currentSelection.campaign.id}`,
            signature: this.currentSignature,
            data: data,
            update: Date.now()
          });
        }
        if (resetFilters) {
          this.resetBusinessPartnerListFilter();
          this.resetLeadFilter();
        }
        // console.log('current filter options are', this.filterOptions.options)
        // console.log('current filter is', this.currentSelection.businessPartnerList.filter)
        // notificationStore.addNotification('Filteroptionen erfolgreich geladen!', 'success');
      } catch (error) {
        notificationStore.addNotification('Filteroptionen konnten nicht geladen werden!', 'error', 35);
        throw error;
      }
    },
    /** pull the reminder data from the backend periodically
    */
    async refreshFollowUps() {
      const notificationStore = useNotificationStore();
      try {
        // console.log('--- > refreshing reminder')
        if ((this.isManagement || this.isSalesTeamManager) && this.currentSelection?.salesAgent?.id === -1) return false;
        const businessPartnerStore = useBusinessPartnersStore();
        const taskList = [
          businessPartnerStore.getByFilter(businessPartnerStore.namedFilter('Reminder:Wiedervorlagen'), [], true),

          // Populate Today/ToDo lists in businessPartnersStore (used by Dashboard counters)
          businessPartnerStore.getByFilter(businessPartnerStore.namedFilter('Today:Wiedervorlagen'), [], true),
          businessPartnerStore.getByFilter(businessPartnerStore.namedFilter('ToDo:Wiedervorlagen'), [], true),
        ];
        this.currentSelection.businessPartnerList.isLoadingToDoFollowUps = true;
        this.currentSelection.businessPartnerList.isLoadingTodayFollowUps = true;
        await Promise.all(taskList);
        this.currentSelection.businessPartnerList.isLoadingToDoFollowUps = false;
        this.currentSelection.businessPartnerList.isLoadingTodayFollowUps = false;
        // notificationStore.addNotification('Wiedervorlagen erfolgreich geladen!', 'success');
      } catch (error) {
        notificationStore.addNotification('Wiedervorlagen konnten nicht geladen werden!', 'error', 36);
        throw error;
      }

    },
    async refreshAppointments() {
      const notificationStore = useNotificationStore();

      try {
        // console.log('--- > refreshing reminder')
        if ((this.isManagement || this.isSalesTeamManager) && this.currentSelection?.salesAgent?.id === -1) return false;
        const businessPartnerStore = useBusinessPartnersStore();
        const taskList = [
          businessPartnerStore.getByFilter(businessPartnerStore.namedFilter('Reminder:VorOrtTermine'), [], true),

          // Populate Today/ToDo lists in businessPartnersStore (used by Dashboard counters)
          businessPartnerStore.getByFilter(businessPartnerStore.namedFilter('Today:VorOrtTermine'), [], true),
          businessPartnerStore.getByFilter(businessPartnerStore.namedFilter('ToDo:VorOrtTermine'), [], true),

        ];
        this.currentSelection.businessPartnerList.isLoadingToDoAppointments = true;
        this.currentSelection.businessPartnerList.isLoadingTodayAppointments = true;
        await Promise.all(taskList);
        this.currentSelection.businessPartnerList.isLoadingToDoAppointments = false;
        this.currentSelection.businessPartnerList.isLoadingTodayAppointments = false;
        notificationStore.addNotification('Termine erfolgreich geladen!', 'success');
      } catch (error) {
        notificationStore.addNotification('Termine konnten nicht geladen werden!', 'error', 37);
        throw error;
      }
    },
    /** sets all options of the businessPartnerList filter to their default values
     * @param {boolean} isOpen if the filter should be open or closed
     */
    resetBusinessPartnerListFilter(isOpen = false) {
      if (!this.currentSelection?.salesAgent || !this.currentSelection.campaign.id) return false
      const filterOption = this.filterOptions.options.find((p) => p.signature === this.currentSignature);
      if (!filterOption) return false;
      this.currentSelection.businessPartnerList.filter = {
        signature: '',
        summary: '',
        idBusinessPartnerStatus: null,
        idBusinessPartnerAboStatus: -1,
        idBusinessPartnerList: -1,
        // idBusinessPartnerProduct: -1,
        idBusinessPartnerProduct: [], // test für daashboard values
        idBusinessPartnerProductSegment: [],
        idBusinessPartnerPotential: -1,
        idBusinessPartnerCityZip: -1,
        nameBusinessPartnerSector: '',
        idBusinessPartnerOrder: -1,
        // NEU als Objekt:
        businessPartnerSalesPitchStatus: { ids: [-1], dateStart: null, dateEnd: null },
        invoice: { dateStart: null, dateEnd: null },
        // Set all product groups to 'beliebig' (2)
        productGroupStates: 'GS:2,TG:2,TB:2,OMP:2,SUM:2',
        revenueMin: null,
        revenueMax: null,
        error: false,
        show: true,
        isOpen: isOpen,
        search: { mode: 'name', string: '', wholeWord: 0 },
        // Set all salesValues to null (beliebig)
        salesValues: { total: null, gs: null, omp: null, oe: null, tb: null },
      };
      // Also update the runtime state for the filter UI if present
      if (this.currentSelection.businessPartnerList.productGroupStates) {
        this.currentSelection.businessPartnerList.productGroupStates = { GS: 2, TG: 2, TB: 2, OMP: 2, SUM: 2 };
      }
      if (this.currentSelection.businessPartnerList.revenueMin !== undefined) {
        this.currentSelection.businessPartnerList.revenueMin = null;
      }
      if (this.currentSelection.businessPartnerList.revenueMax !== undefined) {
        this.currentSelection.businessPartnerList.revenueMax = null;
      }
      if (this.currentSelection.businessPartnerList.revenueRanges) {
        this.currentSelection.businessPartnerList.revenueRanges = {
          GS: { min: null, max: null },
          TG: { min: null, max: null },
          TB: { min: null, max: null },
          OMP: { min: null, max: null },
          SUM: { min: null, max: null }
        };
      }
      // Debug: Logge Filter nach Reset
      console.log('Filter nach resetBusinessPartnerListFilter:', JSON.parse(JSON.stringify(this.currentSelection.businessPartnerList.filter)));
      this.currentSelection.businessPartnerList.page = 1;
      this.currentSelection.businessPartnerList.pages = 1;
      this.currentSelection.businessPartnerList.pageSize = 24;
      this.currentSelection.businessPartnerList.maxRows = 0;
      this.currentSelection.businessPartnerList.title = '';
      // console.log('filter reset to:', this.currentSelection.businessPartnerList.filter)
    },
    /** creates a pulldown for the dashboard where for each list in the segment "Leads" or "Sugar Aktionen" there is a number of unseen businessPartners
     */
    resetLeadFilter() {
      if (!this.currentSelection?.salesAgent || !this.currentSelection.campaign.id) return false
      const filterOption = this.filterOptions.options.find((p) => p.signature === this.currentSignature);
      if (!filterOption) return false;
      // TODO: build the leadList from the filterOptions
      this.currentSelection.leadList.lists = [];
      // console.log('leadLists reset to:', this.currentSelection.leadList.lists)
    }
  },
});
